import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { supabase } from "./supabaseClient";
import Auth from "./components/Auth";
import CaseDetail from "./components/CaseDetail";
import Sidebar from "./components/Sidebar";
import PatientInformation from "./components/PatientInformation";
import SendAppToPatient from "./components/SendAppToPatient";
import {
  Button,
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import "./index.css";

function App() {
  const [user, setUser] = useState(null);
  const [activeView, setActiveView] = useState('patients');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        console.error("Error getting session:", error);
      } else {
        setUser(session?.user ?? null);
      }
    };

    getSession();
  }, []);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const renderMainContent = () => {
    switch (activeView) {
      case 'patients':
        return <PatientInformation />;
      case 'sendapp':
        return <SendAppToPatient />;
      default:
        return <PatientInformation />;
    }
  };

  const drawerWidth = 280;
  const miniDrawerWidth = 64;
  const currentDrawerWidth = sidebarCollapsed ? miniDrawerWidth : drawerWidth;

  return (
    <Router>
      <div className="min-h-screen bg-gray-100">
        {user ? (
          <div className="flex h-screen">
            {/* Mobile App Bar */}
            <AppBar
              position="fixed"
              sx={{
                display: { xs: 'block', md: 'none' },
                zIndex: 1300,
              }}
            >
              <Toolbar>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="h6" noWrap component="div">
                  StrepApp Portal
                </Typography>
              </Toolbar>
            </AppBar>

            {/* Sidebar - Split Screen Left Panel */}
            <div 
              className="hidden md:block bg-white shadow-lg transition-all duration-300 ease-in-out"
              style={{ 
                width: sidebarCollapsed ? `${miniDrawerWidth}px` : `${drawerWidth}px`,
                minWidth: sidebarCollapsed ? `${miniDrawerWidth}px` : `${drawerWidth}px`,
                maxWidth: sidebarCollapsed ? `${miniDrawerWidth}px` : `${drawerWidth}px`,
              }}
            >
              <Sidebar
                activeView={activeView}
                onViewChange={setActiveView}
                open={false}
                onClose={() => {}}
                isCollapsed={sidebarCollapsed}
                onToggleCollapse={handleSidebarToggle}
                isSplitScreen={true}
              />
            </div>

            {/* Mobile Sidebar Drawer */}
            <Sidebar
              activeView={activeView}
              onViewChange={setActiveView}
              open={mobileOpen}
              onClose={handleDrawerToggle}
              isCollapsed={false}
              onToggleCollapse={() => {}}
              isSplitScreen={false}
            />

            {/* Main Content - Split Screen Right Panel */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Sign Out Button */}
              <div className="absolute top-4 right-4 z-10">
                <Button
                  onClick={handleSignOut}
                  variant="contained"
                  color="secondary"
                  className="font-bold"
                  sx={{ mt: { xs: 7, md: 0 } }}
                >
                  Sign Out
                </Button>
              </div>
              
              {/* Content Area */}
              <div className="flex-1 overflow-auto" style={{ marginTop: '0px' }}>
                <Routes>
                  <Route
                    path="/"
                    element={renderMainContent()}
                  />
                  <Route path="/case/:id" element={<CaseDetail />} />
                </Routes>
              </div>
            </div>
          </div>
        ) : (
          <Auth setUser={setUser} />
        )}
      </div>
    </Router>
  );
}

export default App;
